import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Outlet, useLocation } from 'react-router-dom';
import { experimentalStyled } from '@material-ui/core/styles';
import { useTheme } from '@material-ui/core';
import FrontendFooter from './FrontendFooter'
import FrontendNavbar from './FrontendNavbar'
import FrontendSidebar from './FrontendSidebar'
import AlternateNavbar from './AlternateNavbar';
import useAPI from '../../hooks/useAPI'
import useAuth from '../../hooks/useAuth'
import { Market } from '../../lib/backendApi/types/market';
import toast from 'react-hot-toast';

const FrontendLayoutRoot = experimentalStyled('div')(({ theme, hideNav }) => ({
  minHeight: '100vh',
  paddingTop: hideNav ? 0 : 72, // Match fixed navbar height, or 0 if hidden
  paddingBottom: hideNav ? 0 : 64,
  marginBottom: hideNav ? 0 : -64,
}));

const FrontendLayout = ({ children }) => {
  const location = useLocation();
  const [isSidebarMobileOpen, setIsSidebarMobileOpen] = useState(false);
  const theme = useTheme()
  const [markets, setMarkets] = useState([])
  const [announcement, setAnnouncement] = useState()
  const { api } = useAPI()
  const { isAuthenticated, isMarketWideUser, user } = useAuth()

  // Check if we're on the presentation editor route
  const isPresentationEditor = location.pathname.startsWith('/presentations/') &&
    location.pathname !== '/presentations' &&
    location.pathname !== '/presentations/';

  /**
   * 
   * @param {AbortSignal} abort 
   * @returns 
   */
  const getMarkets = async (abort) => {
    try {
      /** @type Market[] */
      const marketRes = [];
      let hasMore = true;
      let page = 0;
      let pageLimit = 250;
      while(hasMore) {
        // NOTE: Market controller automatically filters users markets based on what they should see.
        const filter = {
          where: {
            and: [
              { isActive: true },
              { company: { exists: true } }
            ]
          },
          include: [
            {
              relation: "company",
              scope: {
                where: { isActive: true }
              }
            },
            { relation: "conciergeAuthorizedMarkets" }
          ],
          order: "name ASC",
          limit: pageLimit,
          skip: page*pageLimit
        };
        const response = await api.Market.find(filter, abort);

        if (response.hasOwnProperty("error")) {
          console.error("Unable to get markets", response);
          toast.error("Error getting market data");
          return;
        }

        const data = response.filter(market => market.company);

        if (marketRes.hasOwnProperty("error")) {
          console.error("Unable to get markets", marketRes)
          toast.error("Error getting market data");
          return
        };

        marketRes.push(...data);

        if(data.length === 250) {
          page += 1;
        } else {
          hasMore = false;
        }
      }

      if (isMarketWideUser && user.additionalMarkets) {
        user.additionalMarkets.forEach(am => {
          // Do not include additional markets that are not active.
          if(!am.isActive && !am.company?.isActive) return;
          // Only add additional markets that are not in the initial markets results.
          if (!marketRes.find(market => market.id === am.id)) {
            marketRes.push(am);
          };
        });
      }
      setMarkets(marketRes);
    } catch (err) {
      if (abort.aborted) return
      console.error(err)
    }
  }

  const getAnnouncement = async (abort) => {
    if (sessionStorage.getItem('hideAnnouncement')) return
    try {
      const data = await api.SiteAnnouncement.get(abort)
      if (data.error) {
        console.error("Unable to get announcement", data)
        return
      }
      if (Object.keys(data).length === 0) return
      setAnnouncement(data)
    } catch (err) {
      if (abort.aborted) return
      console.error(err)
    }
  }

  const hideAnnouncement = () => {
    setAnnouncement(null)
    sessionStorage.setItem("hideAnnouncement", "true")
  }

  useEffect(() => {
    if (!isAuthenticated) return

    const controller = new AbortController()
    getMarkets(controller.signal)
    getAnnouncement(controller.signal)
    return () => controller.abort()

  }, [isAuthenticated])

  return (
    <>
      <FrontendLayoutRoot hideNav={isPresentationEditor}>
        {!isPresentationEditor && (
          <>
            {theme.name != "AudacyFlash" && theme.name != "CreativeLove"
              ? <FrontendNavbar onSidebarMobileOpen={() => setIsSidebarMobileOpen(true)} markets={markets} announcement={announcement} hideAnnouncement={hideAnnouncement} />
              : <AlternateNavbar onSidebarMobileOpen={() => setIsSidebarMobileOpen(true)} markets={markets} announcement={announcement} hideAnnouncement={hideAnnouncement} />
            }
            <FrontendSidebar onMobileClose={() => setIsSidebarMobileOpen(false)} openMobile={isSidebarMobileOpen} markets={markets} announcement={announcement} hideAnnouncement={hideAnnouncement} />
          </>
        )}

        {children || <Outlet />}

      </FrontendLayoutRoot>
      {!isPresentationEditor && <FrontendFooter />}
    </>
  );
};

FrontendLayout.propTypes = {
  children: PropTypes.node
};

export default FrontendLayout;
